import type { API_CreatePlatformPlanRequest } from "@pilatestime-admin/shared-types";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { ArrowLeft } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useCreatePlatformPlan } from "@/api/platformPlans";
import { useApiParameters } from "@/hooks/useApiParameters";
import { PlanForm } from "./-components/PlanForm";

export const Route = createFileRoute("/_auth/app/_app/platform-plans/new")({
	loader: async ({ context }) => {
		const { userId, token } = useApiParameters(
			context.userId,
			await context.get_token(),
		);
		return { userId, token };
	},
	component: NewPlatformPlanPage,
});

function NewPlatformPlanPage() {
	const { t } = useTranslation();
	const { token } = Route.useLoaderData();
	const navigate = useNavigate();
	const createMutation = useCreatePlatformPlan();

	const handleSubmit = (body: API_CreatePlatformPlanRequest | unknown) => {
		createMutation.mutate(
			{ token, body: body as API_CreatePlatformPlanRequest },
			{
				onSuccess: (created) => {
					navigate({
						to: "/app/platform-plans/$planId",
						params: { planId: created.id },
					});
				},
			},
		);
	};

	return (
		<div className="container mx-auto p-6 space-y-6">
			<div className="flex items-center gap-3">
				<Link
					to="/app/platform-plans"
					className="btn btn-ghost btn-sm"
					data-testid="platform-plans-new-back"
				>
					<ArrowLeft className="h-4 w-4" />
					{t("common.back")}
				</Link>
				<h1 className="text-2xl font-bold text-base-content">
					{t("platformPlans.new.title")}
				</h1>
			</div>

			<div className="card bg-base-100 shadow-sm">
				<div className="card-body">
					<PlanForm
						mode="create"
						onSubmit={handleSubmit}
						isSubmitting={createMutation.isPending}
						onCancel={() => navigate({ to: "/app/platform-plans" })}
					/>
				</div>
			</div>
		</div>
	);
}
